import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { LabelWidget } from '../LabelWidget';
import { RadioButtonWithLabelWidget } from './SelectorShims';

export enum RadioLayoutType {
  Veritical = 0,
  Horizontal = 1,
}

export interface RadioButtonWithLabelGroupProps<T = string> extends Pick<React.AriaAttributes, 'aria-labelledby'> {
  /** The description of the radio button group that will be read by screen readers */
  ariaLabel?: string;
  /** The list of Radio Button Options to be created */
  buttonList: RadioButtonWithLabelOption<T>[];
  /** Which radio button should be checked */
  checkedIndex?: number;
  /** Callback to being changed */
  handleChange: (value: T, checkedIndex: number) => void;
  /** whether the options should be justified and evenly spaced horizontally */
  justifyHorizontalItems?: boolean;
  /** Label to display above the radio group, if any. If omitted, you must instead supply `ariaLabel` or `aria-labelledby` */
  label?: React.ReactNode;
  /** whether the options should be displayed in a horizontal or vertical layout */
  layout?: RadioLayoutType;
  /** The group name attribute.  If not given, a unique name will be generated */
  name?: string;
  /** If the default left margin should be removed */
  removeLeftMargin?: boolean;
  testId?: string;
}

export interface RadioButtonWithLabelOption<T = string> {
  /** Disabled state of the radio option */
  disabled?: boolean;
  /** Label of the radio option */
  label: React.ReactNode;
  /** Test id to be attached to the radio option */
  testId: string;
  /** Value returned in the `handleChange` event triggered by selecting this radio option */
  value: T;
}

const StyleName = TypeStyle.style({
  $nest: {
    '&.horizontal': { display: 'flex', flexWrap: 'wrap' },
    '&.horizontal.justify': { justifyContent: 'space-between' },
    '&.horizontal > :not(:last-child)': { marginRight: TabStyles.Spacing.ExtraLargeSpace },
  }
});

/** Displays a group of radio options */
export class RadioButtonWithLabelGroupWidget<T = string> extends React.Component<RadioButtonWithLabelGroupProps<T> & Omit<ElemAttrs, 'role'>> {
  private groupId: string = generateId();

  public constructor(props: RadioButtonWithLabelGroupWidget<T>['props']) {
    super(props);
    if (!props.label && !(props.ariaLabel || props['aria-label']) && !props['aria-labelledby']) {
      // tslint:disable-next-line:no-console
      console.trace('`label`, `aria-label`, or `aria-labelledby` must be provided for every radio group');
    }
  }

  public render(): JSX.Element {
    // tslint:disable-next-line:max-line-length
    const { ariaLabel, buttonList, checkedIndex, handleChange, justifyHorizontalItems, label, layout, name, removeLeftMargin, testId, ...htmlAttrs } = this.props;

    const buttons = buttonList.map((buttonOption, index) =>
      <RadioButtonWithLabelWidget
        {...buttonOption}
        checked={index === checkedIndex}
        groupName={name || this.groupId}
        handleChange={handleChange}
        index={index}
        key={index}
        removeLeftMargin={removeLeftMargin}
      />
    );

    const labelId = label ? this.groupId + '-label' : undefined;

    return (
      <div
        data-tb-test-id={makeTestId('RadioButtonWithLabelGroupWidget', testId!)}
        {...htmlAttrs}
        aria-label={ariaLabel ?? htmlAttrs['aria-label']}
        aria-labelledby={htmlAttrs['aria-labelledby'] ?? labelId}
        className={TypeStyle.classes(StyleName, htmlAttrs.className, {
          horizontal: layout === RadioLayoutType.Horizontal,
          justify: justifyHorizontalItems,
        })}
        role='radiogroup'
      >
        {label && <LabelWidget children={label} id={labelId} />}
        {buttons}
      </div>
    );
  }
}
